import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { AfrStatus, Company, CompanyAfrFiling } from "@somasi/shared";
import {
  AFR_STATUSES,
  DEFAULT_AFR_HORIZON_DAYS,
  MAX_PAGE_SIZE,
  afrUrgency,
  businessToday,
  displayFyLabel,
} from "@somasi/shared";
import { api } from "@/integrations/aws/resources";
import { PageHeader } from "@/components/app-shell";
import { AfrExportDialog } from "@/components/afr-export-dialog";
import { AfrEditPeriodDialog } from "@/components/afr-edit-period-dialog";
import { AfrFileDialog } from "@/components/afr-file-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RowAction, RowActions } from "@/components/row-actions";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ClientGroupSelect } from "@/components/entity-select";
import {
  describeDateRange,
  EMPTY_DATE_RANGE,
  FilterSelect,
  isDateRangeSet,
  ListPage,
  ListPageHeader,
  ListPageTable,
  ListSearch,
  ListToolbar,
  MultiDateRangeFilter,
  SortHeader,
  TableMessageRow,
  TablePagination,
  useDebounced,
  useListSort,
  usePagination,
  type DateRange,
  type DateRangeField,
} from "@/components/list-controls";
import { useDeleteAfrPeriod } from "@/lib/afr-delete";
import { AFR_STATUS_LABEL, AFR_STATUS_TONE, AFR_URGENCY_LABEL, AFR_URGENCY_TONE } from "@/lib/afr";
import { useAuth } from "@/lib/auth";
import { Download, FileCheck2, Paperclip, Pencil, Trash2 } from "lucide-react";
import { formatDate } from "@/lib/utils";

/**
 * The AFR register — every company's financial years, and where each one stands.
 *
 * The screen that the single `afr_due_date` column could not produce. With one
 * mutable date per company, "who has filed FY2025-26" had no answer: the
 * companies that had showed next year's dates, the ones that had not showed this
 * year's, and nothing on the row said which of the two you were reading.
 *
 * ## Which date the filter is on
 *
 * The one control that matters here, and the reason it is a choice rather than a
 * fixed column. These are three different questions:
 *
 *   Year-end     a fixed financial year, whatever each company's deadline is.
 *                "The FY2025-26 picture." The only one that can report on a
 *                year, because two companies with different year-ends share no
 *                due date at all.
 *   AFR due      a deadline window, whatever year each company is in. "What is
 *                due in the next three months."
 *   Filing date  what was actually submitted in a period. For billing, and for
 *                asking how much got done last quarter.
 *
 * None is a safe default, so the field is picked and named on screen, and the
 * export carries the same phrase so the file and the screen describe one set.
 */

export const Route = createFileRoute("/_authenticated/afr")({
  component: AfrRegisterPage,
  /**
   * `?company=<id>` narrows the register to one company's financial years.
   *
   * Which is what makes this one module rather than two: the company page links
   * straight into its own history here, instead of the operator arriving at an
   * 805-row register and searching for the name they just had open.
   */
  validateSearch: (search: Record<string, unknown>): { company?: string } => ({
    company: typeof search.company === "string" && search.company ? search.company : undefined,
  }),
});

/** The filing dates the register filters on, keyed by their column. */
const AFR_DATE_FIELDS: DateRangeField[] = [
  { key: "period_end", label: "Year-end" },
  { key: "afr_due_date", label: "AFR due" },
  { key: "afr_filing_date", label: "Filed" },
];

const NO_DATE_RANGES: Record<string, DateRange> = Object.fromEntries(
  AFR_DATE_FIELDS.map((f) => [f.key, EMPTY_DATE_RANGE]),
);

function AfrRegisterPage() {
  const { company: companyParam } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { canEdit } = useAuth();
  const deleteAfrPeriod = useDeleteAfrPeriod();
  const qc = useQueryClient();
  const today = businessToday();

  /*
   * Three ranges behind one "Dates" button, a tab each.
   *
   * Separate ranges rather than one range over a chosen column, because they
   * combine: "filed during Q3, for the year ending March 2026" is an ordinary
   * thing to want, and a single shared range could not express it.
   */
  const [dateRanges, setDateRanges] = useState<Record<string, DateRange>>(NO_DATE_RANGES);
  const periodEndRange = dateRanges.period_end ?? EMPTY_DATE_RANGE;
  const dueRange = dateRanges.afr_due_date ?? EMPTY_DATE_RANGE;
  const filedRange = dateRanges.afr_filing_date ?? EMPTY_DATE_RANGE;
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [groupFilter, setGroupFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const [filing, setFiling] = useState<{ row: AfrRow } | null>(null);
  const [editing, setEditing] = useState<{ row: AfrRow } | null>(null);

  const query = useDebounced(search.trim());
  const sort = useListSort({ column: "afr_due_date", ascending: true });

  const filters = {
    companyParam,
    periodEndRange,
    dueRange,
    filedRange,
    statusFilter,
    groupFilter,
    query,
    sort: sort.sort,
  };
  const pager = usePagination({ resetKey: filters });

  /** The company the register was narrowed to, for the banner's name. */
  const { data: focusCompany } = useQuery({
    queryKey: ["afr-focus-company", companyParam],
    enabled: Boolean(companyParam),
    queryFn: async () => {
      const { data, error } = await api.companies.get(companyParam!, ["id", "legal_name"]);
      if (error) throw new Error(error.message);
      return data as Pick<Company, "id" | "legal_name"> | null;
    },
  });

  /** The filings the filters describe — the table pages it, the export takes it whole. */
  const filingList = () => {
    let list = api.afrFilings.list();
    // One company's history, where the page was reached from that company.
    // Server-side, unlike the name search below, because the id is a real column
    // on the filing — so this narrows the whole register rather than the page.
    if (companyParam) list = list.eq("company_id", companyParam);
    if (statusFilter !== "all") list = list.eq("status", statusFilter);

    for (const [column, range] of [
      ["period_end", periodEndRange],
      ["afr_due_date", dueRange],
      ["afr_filing_date", filedRange],
    ] as const) {
      if (range.from) list = list.gte(column, range.from);
      if (range.to) list = list.lte(column, range.to);
    }

    // A filing-date window implies the filed ones: a row with no filing date
    // cannot fall inside a window of filing dates, and leaving it in would make
    // "what did we file in Q3" return every open period as well.
    if (isDateRangeSet(filedRange)) list = list.not("afr_filing_date", "is", null);

    /*
     * Ordered by the column heading chosen, the deadline by default, whichever
     * filters are on. The alternative — sorting by whichever range happens to be
     * set — means the table reorders itself when you narrow it, which is
     * disorienting for no gain. The deadline breaks ties so rows sharing a status
     * or a filing date keep a stable order from page to page.
     */
    list = list.order(sort.sort.column, { ascending: sort.sort.ascending });
    if (sort.sort.column !== "afr_due_date") list = list.order("afr_due_date", { ascending: true });
    return list.order("id", { ascending: true });
  };

  const { data: result, isFetching } = useQuery({
    queryKey: ["afr-filings", { ...filters, page: pager.page, pageSize: pager.pageSize }],
    queryFn: async () => {
      const { data, error, count } = await filingList().page(pager.page, pager.pageSize);
      if (error) throw error;
      return { rows: (data ?? []) as CompanyAfrFiling[], total: count ?? 0 };
    },
    placeholderData: (previous) => previous,
  });

  const filings = result?.rows ?? [];

  const removeYear = async (filing: CompanyAfrFiling, companyName?: string) => {
    if (!(await deleteAfrPeriod(filing, companyName))) return;
    qc.invalidateQueries({ queryKey: ["afr-filings"] });
    qc.invalidateQueries({ queryKey: ["companies"] });
  };

  /*
   * The companies behind the page of filings, in one read rather than one per
   * row. A filing carries no company name — denormalising it would mean every
   * rename restamping a company's whole history — so the names are joined here,
   * for the page on screen only.
   */
  const companyIds = useMemo(
    () => [...new Set(filings.map((filing) => filing.company_id))],
    [filings],
  );

  const { data: companies } = useQuery({
    queryKey: ["afr-companies", companyIds],
    enabled: companyIds.length > 0,
    queryFn: async () => {
      const { data, error } = await api.companies
        .list()
        .in("id", companyIds)
        .limit(MAX_PAGE_SIZE)
        .all();
      if (error) throw new Error(error.message);
      return new Map(((data ?? []) as Company[]).map((company) => [company.id, company]));
    },
  });

  const rows: AfrRow[] = filings.map((filing) => ({
    filing,
    company: companies?.get(filing.company_id) ?? null,
  }));

  // The group and name filters apply to the company, which the filings table
  // does not carry — so they narrow the joined page rather than the query. Said
  // plainly on screen, because a filter that only sees the current page is a
  // filter that lies if nobody says so.
  const visible = rows.filter(({ company }) => {
    if (groupFilter !== "all" && company?.client_group_id !== groupFilter) return false;
    if (!query) return true;
    const haystack =
      `${company?.legal_name ?? ""} ${company?.registration_number ?? ""}`.toLowerCase();
    return haystack.includes(query.toLowerCase());
  });

  const narrowedOnPage =
    (groupFilter !== "all" || Boolean(query)) && visible.length !== rows.length;

  /*
   * Every filter in words — including the ones that narrow companies rather
   * than financial years.
   *
   * The export dialog decides its default scope from whether this is empty, so
   * a summary mentioning only the dates and the status meant narrowing by
   * client group or by name and then being handed the whole register, with the
   * file's own "filters" line agreeing that nothing had been narrowed.
   */
  const filterSummary =
    [
      companyParam ? `company ${focusCompany?.legal_name ?? companyParam}` : null,
      groupFilter !== "all" ? "one client group" : null,
      query ? `matching “${query}”` : null,
      describeDateRange("Year-end", periodEndRange),
      describeDateRange("AFR due", dueRange),
      describeDateRange("Filed", filedRange),
      statusFilter !== "all" ? `status ${AFR_STATUS_LABEL[statusFilter as AfrStatus]}` : null,
    ]
      .filter(Boolean)
      .join(", ") || null;

  return (
    <ListPage>
      <ListPageHeader>
        <PageHeader
          title="AFR Register"
          description="Every company's financial years, what each one owes, and what has been filed."
          actions={
            <Button variant="outline" onClick={() => setExportOpen(true)}>
              <Download className="mr-2 h-4 w-4" /> Export
            </Button>
          }
        />

        <ListToolbar>
          <ListSearch
            value={search}
            onChange={setSearch}
            placeholder="Company name or registration no.…"
          />

          <MultiDateRangeFilter
            fields={AFR_DATE_FIELDS}
            value={dateRanges}
            onChange={setDateRanges}
            className="w-64"
          />

          <FilterSelect
            value={statusFilter}
            onChange={setStatusFilter}
            className="w-36"
            options={[
              { value: "all", label: "All statuses" },
              ...AFR_STATUSES.map((status) => ({ value: status, label: AFR_STATUS_LABEL[status] })),
            ]}
          />

          <ClientGroupSelect
            value={groupFilter === "all" ? "" : groupFilter}
            onChange={(id) => setGroupFilter(id || "all")}
            clearable
            clearLabel="All client groups"
            placeholder="All client groups"
            className="w-56"
          />

          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setDateRanges(NO_DATE_RANGES);
              setStatusFilter("all");
              setGroupFilter("all");
              setSearch("");
            }}
          >
            Clear
          </Button>
        </ListToolbar>

        {companyParam && (
          <div className="mb-3 flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
            <span>
              Showing one company's financial years
              {focusCompany ? (
                <>
                  : <strong>{focusCompany.legal_name}</strong>
                </>
              ) : null}
              .
            </span>
            <Button
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={() => void navigate({ search: {} })}
            >
              Show every company
            </Button>
          </div>
        )}

        {narrowedOnPage && (
          <p className="pb-2 text-xs text-muted-foreground">
            The company name and client group narrow this page of results, not the whole register —
            use the date and status filters to narrow it at the source.
          </p>
        )}
      </ListPageHeader>

      <ListPageTable>
        <Table containerClassName="min-h-0 flex-1">
          <TableHeader sticky>
            <TableRow>
              <TableHead>Company</TableHead>
              <TableHead>Registration no.</TableHead>
              {/* Company and registration number come from the joined company, not the filing, so
                  the API cannot order the filings by them. */}
              <SortHeader sort={sort} column="period_end">
                Financial year
              </SortHeader>
              <SortHeader sort={sort} column="afr_due_date">
                AFR due
              </SortHeader>
              <SortHeader sort={sort} column="afr_filing_date">
                Filed
              </SortHeader>
              <SortHeader sort={sort} column="status">
                Status
              </SortHeader>
              <TableHead className="w-px whitespace-nowrap text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.length === 0 && (
              <TableMessageRow colSpan={7}>
                {isFetching
                  ? "Loading…"
                  : filterSummary
                    ? "No financial years match these filters."
                    : "No financial years recorded yet."}
              </TableMessageRow>
            )}
            {visible.map(({ filing, company }) => {
              const open = filing.afr_filing_date == null && filing.status === "pending";
              const urgency = open
                ? afrUrgency(filing.afr_due_date, today, DEFAULT_AFR_HORIZON_DAYS)
                : null;

              return (
                <TableRow key={filing.id}>
                  <TableCell className="font-medium">
                    {company ? (
                      // Straight to Compliance: somebody clicking a company from
                      // a financial year came for its AFR, not its trading name.
                      <Link
                        to="/companies/$companyId"
                        params={{ companyId: company.id }}
                        search={{ tab: "compliance" }}
                        className="hover:underline"
                      >
                        {company.legal_name}
                      </Link>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {company?.registration_number ?? ""}
                  </TableCell>
                  {/*
                    The label with the dates it stands for, never on its own.
                    Two companies can both be in "2026-27" and mean different
                    twelve months — one closing 31 March, another 28 February —
                    so a bare label is a question rather than an answer. The
                    grouping is still right: each is that company's own 2026-27.
                  */}
                  <TableCell className="whitespace-nowrap">
                    <span className="block font-medium">{displayFyLabel(filing.fy_label)}</span>
                    <span className="block text-xs text-muted-foreground">
                      {formatDate(filing.period_start)} – {formatDate(filing.period_end)}
                    </span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <span className="flex items-center gap-2">
                      {formatDate(filing.afr_due_date)}
                      {urgency && urgency !== "upcoming" && (
                        <Badge className={AFR_URGENCY_TONE[urgency]}>
                          {AFR_URGENCY_LABEL[urgency]}
                        </Badge>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatDate(filing.afr_filing_date)}
                  </TableCell>
                  <TableCell>
                    <Badge className={AFR_STATUS_TONE[filing.status]}>
                      {AFR_STATUS_LABEL[filing.status]}
                    </Badge>
                  </TableCell>
                  {/* The cell always renders, so the column count matches the
                      header for every reader. Documents are open to anyone who
                      may read the register — downloading the accounts is not an
                      edit — while filing and correcting are not. The panel
                      itself gates uploading and deleting the same way. */}
                  <TableCell className="whitespace-nowrap">
                    {/* One way into a year, not two. The dialog carries the
                        filing fields while there is a filing to record, and the
                        documents always — so opening it to attach a statement
                        also shows the deadline, which two separate actions
                        could never do. */}
                    <RowActions>
                      {canEdit && open ? (
                        <RowAction
                          label="Mark filed"
                          onClick={() => setFiling({ row: { filing, company } })}
                        >
                          <FileCheck2 />
                        </RowAction>
                      ) : (
                        <RowAction
                          label="Documents"
                          onClick={() => setFiling({ row: { filing, company } })}
                        >
                          <Paperclip />
                        </RowAction>
                      )}
                      {canEdit && (
                        <RowAction
                          label="Edit"
                          onClick={() => setEditing({ row: { filing, company } })}
                        >
                          <Pencil />
                        </RowAction>
                      )}
                      {canEdit && (
                        <RowAction
                          label="Delete"
                          destructive
                          onClick={() => void removeYear(filing, company?.legal_name)}
                        >
                          <Trash2 />
                        </RowAction>
                      )}
                    </RowActions>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          pagination={pager}
          total={result?.total}
          unit="financial year"
          units="financial years"
        />
      </ListPageTable>

      <AfrFileDialog
        open={filing !== null}
        onOpenChange={(next) => !next && setFiling(null)}
        filing={filing?.row.filing ?? null}
        company={filing?.row.company ?? null}
        onFiled={() => {
          setFiling(null);
          qc.invalidateQueries({ queryKey: ["afr-filings"] });
          qc.invalidateQueries({ queryKey: ["companies"] });
        }}
      />

      <AfrEditPeriodDialog
        open={editing !== null}
        onOpenChange={(next) => !next && setEditing(null)}
        filing={editing?.row.filing ?? null}
        company={editing?.row.company ?? null}
        onSaved={() => {
          setEditing(null);
          qc.invalidateQueries({ queryKey: ["afr-filings"] });
          qc.invalidateQueries({ queryKey: ["companies"] });
        }}
      />

      <AfrExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        filteredCount={result?.total}
        filterSummary={filterSummary}
        fetchFiltered={async () => {
          const { data, error } = await filingList().all();
          if (error) throw new Error(error.message);
          return (data ?? []) as CompanyAfrFiling[];
        }}
        fetchCompanies={async (scope) => {
          /*
           * The companies the Register shape lists.
           *
           * Narrowed by the same company-side filters the page applies, because
           * that shape is driven from the company list: leaving it unfiltered
           * returned the whole book however narrow the filings were, which is
           * exactly what a filtered export is not.
           *
           * The date, year and status filters are deliberately *not* applied
           * here. They select financial years, and a company whose years none
           * of them match is precisely the company the register is meant to
           * show — with a blank filing date saying it has not filed.
           */
          let list = api.companies.list().limit(MAX_PAGE_SIZE);
          if (scope === "filtered") {
            if (companyParam) list = list.eq("id", companyParam);
            if (groupFilter !== "all") list = list.eq("client_group_id", groupFilter);
            if (query) {
              list = list.or([
                { column: "legal_name", op: "ilike", value: `%${query}%` },
                { column: "registration_number", op: "ilike", value: `%${query}%` },
              ]);
            }
          }
          const { data, error } = await list.all();
          if (error) throw new Error(error.message);
          return new Map(((data ?? []) as Company[]).map((company) => [company.id, company]));
        }}
      />
    </ListPage>
  );
}

/** One row on screen: the financial year, and the company it belongs to. */
interface AfrRow {
  filing: CompanyAfrFiling;
  company: Company | null;
}
